import { HydratedChatbotAsset } from "@zapier/bff";
import { BetaDataTableButton as Button, Tooltip } from "@zapier/design-system";
import { useTranslations } from "@zapier/i18n";
import {
  DateCell,
  LocationCell,
  OwnerCell,
  Row,
  TitleCell,
} from "@zapier/shared";
import { AssetTypes, getChatbotBuilderUrl } from "@zapier/utils";
import { Icon } from "@zapier/zinnia-icons";
import { useCallback } from "react";
import { HydratedChatbotWithFolder } from "../useChatbotsList";
import { ChatbotModal } from "./ChatbotModals";
import styles from "./ChatbotsTable.module.css";
import { ConversationCell } from "./ConversationCell";
import { useChatbotRow } from "./useChatbotRow";

interface ChatbotRowProps {
  allowsSelection?: boolean;
  chatbot: HydratedChatbotWithFolder;
  searchQuery?: string | null;
  setCurrentModal: (modal: ChatbotModal | undefined) => void;
  setCurrentChatbot: (
    chatbot: HydratedChatbotWithFolder | HydratedChatbotAsset | undefined,
  ) => void;
  showLocation: boolean;
  onCopyToAnotherAccount?: (asset: HydratedChatbotAsset) => void;
}

const ChatbotRow = ({
  allowsSelection,
  chatbot,
  searchQuery,
  setCurrentModal,
  setCurrentChatbot,
  showLocation,
  onCopyToAnotherAccount,
}: ChatbotRowProps) => {
  const t = useTranslations("chatbots-table");
  const tShared = useTranslations("fallbacks");
  const { menuItems, triggerPermissionsCheck } = useChatbotRow({
    chatbot,
    setCurrentModal,
    setCurrentChatbot,
    onCopyToAnotherAccount,
  });

  const chatbotId = chatbot.id ?? "";
  const isLocked = chatbot.isLocked ?? false;
  const chatbotName =
    chatbot.name || tShared("untitled-asset", { assetType: "Chatbot" });

  const handleUpgradeChatbot = useCallback(() => {
    setCurrentChatbot(chatbot);
    setCurrentModal(ChatbotModal.Upgrade);
  }, [chatbot, setCurrentChatbot, setCurrentModal]);

  return (
    <Row
      allowsSelection={allowsSelection}
      id={chatbotId}
      assetType={AssetTypes.Chatbot}
      menuItems={menuItems}
      onOpenMenu={triggerPermissionsCheck}
      assetName={chatbotName}
      kebabMenuLabel={t("kebab-menu-label")}
      kebabMenuAriaLabel={t("kebab-menu-label-for", { name: chatbotName })}
    >
      <TitleCell
        id={chatbotId}
        url={getChatbotBuilderUrl(chatbotId)}
        title={chatbot.name}
        icon="navAIChatbot"
        searchQuery={searchQuery}
        assetType={AssetTypes.Chatbot}
        badge={
          isLocked ? (
            <Tooltip content={t("locked.tooltip")} allowMultiline>
              <Button
                className={styles.upgradeBadge}
                onPress={handleUpgradeChatbot}
                aria-label={t("locked.tooltip")}
              >
                <Icon name="actionLock" size={12} />
                {t("locked.badge")}
              </Button>
            </Tooltip>
          ) : undefined
        }
      />

      <ConversationCell count={chatbot.conversations ?? 0} />

      <TitleCell id={chatbotId} title={chatbot.chatModelId} />

      {showLocation && (
        <LocationCell
          folderId={chatbot.folder?.id}
          folderName={chatbot.folder?.name}
          ownerId={chatbot.folder?.ownerId}
        />
      )}

      <DateCell datetime={(chatbot.updatedAt || chatbot.createdAt) ?? ""} />

      <OwnerCell
        name={chatbot.owner?.fullName}
        photoUrl={chatbot.owner?.photoUrl}
      />
    </Row>
  );
};

export default ChatbotRow;
